/**
 * `AIContextEngineModel.Core.ts` — port of Unity's
 * `AIContextEngineModel.Core.cs` (interface + cache + interaction state).
 * Digest builders live in `AIContextEngineModel.SceneProjectUserContext.ts`,
 * mirroring the Unity partial split.
 *
 * Web slice deferral: the hierarchy helper agent (`HierarchyTransport`) is
 * not ported — `querySceneContext` runs local string matching only. The
 * knowledge/docs agent (`KnowledgeTransport`) IS ported; see
 * `AIContextEngineModel.KnowledgeTransport.ts`.
 */

import { Emitter } from "@core/events/emitter";
import { Ioc } from "@core/ioc/ioc";
import { animationDecodeGeneration } from "@runtime/assets/animationClips";
import type { IAuthModel } from "@models/auth/IAuthModel";
import { AUTH_MODEL } from "@models/auth/IAuthModel";
import type { IProjectDocsModel } from "@models/docs/IProjectDocsModel";
import { PROJECT_DOCS_MODEL } from "@models/docs/IProjectDocsModel";
import type { IProjectModel } from "@models/project/IProjectModel";
import type { ISelectionModel } from "@models/scene/ISelectionModel";
import { HOVER_MODEL, type IHoverModel } from "@models/scene/IHoverModel";
import { normalizeProductNameKey } from "@models/data/productDocumentDto";
import type { IProjectWorkflowModel } from "@models/workflow/IProjectWorkflowModel";
import { PROJECT_WORKFLOW_MODEL } from "@models/workflow/IProjectWorkflowModel";
import type { ICustomerVectorStoreModel } from "@ai/memory/ICustomerVectorStoreModel";
import { CUSTOMER_VECTOR_STORE_MODEL } from "@ai/memory/ICustomerVectorStoreModel";
import {
  aliasSegmentOf,
  buildPathIndex,
  displayNameOf,
  isMoved,
  normalizePath,
} from "./hierarchyPaths";
import {
  buildProjectContextText,
  buildSceneContextText,
  buildUserContextText,
  hasUserInteractionContext,
  type UserInteractionSnapshot,
  type UserInteractionState,
} from "./AIContextEngineModel.SceneProjectUserContext";
import {
  buildDocsInstruction,
  buildDocsPrompt,
  findPdfAsync,
  type KnowledgeContextResponse,
  type KnowledgeDeps,
  queryKnowledgeAsync,
  searchDocsByFileNameAsync,
} from "./AIContextEngineModel.KnowledgeTransport";

export interface IAIContextEngineModel {
  readonly onContextInvalidated: Emitter<void>;

  buildSceneContext(): string;
  buildProjectContext(): string;
  buildUserContext(): string;

  /** Immutable snapshot for durable question-time transcript context. */
  captureUserInteractionState(): UserInteractionSnapshot;
  /** Web snapshot-id equivalent of Unity's `SetCurrentlyPointedAtObject`. */
  setPointedSnapshotId(snapshotId: string | null): void;
  hasUserInteractionContext(): boolean;
  tryBuildUserContextForMessage(): string | null;
  tryBuildProjectContextForMessage(): string | null;

  /** Local hierarchy lookup backing `Get_Scene_Context`. */
  querySceneContext(prompt?: string | null, searchQuery?: ReadonlyArray<string> | null): string;
  /**
   * Saved notes + document findings for one part, backing `Get_Part_Context`.
   *
   * Async on web (Unity's is too — `Core.cs:153`): it now consults the
   * knowledge agent for document context the way Unity does, rather than
   * returning notes alone.
   */
  queryPartContextAsync(prompt: string, partPath: string): Promise<PartContextQueryResult>;

  // -- Knowledge (documents) — Unity `AIContextEngineModel.Types.cs:17-27` --

  /** Appends the docs instruction to a prompt unless it already mentions docs. */
  buildDocsPrompt(prompt: string): string;
  /** Semantic document search over the customer vector store. */
  queryKnowledgeContextAsync(
    prompt: string,
    partContext: string,
    semanticOnly?: boolean,
    signal?: AbortSignal,
  ): Promise<KnowledgeContextResponse>;
  /** Filename-only document search over the library manifest (no model call). */
  searchDocsByFileNameAsync(prompt: string): Promise<KnowledgeContextResponse>;
  /** Resolve one PDF to open from a filename or short query. */
  findPdfAsync(prompt: string, signal?: AbortSignal): Promise<KnowledgeContextResponse>;

  invalidateCache(): void;
  dispose(): void;
}

/** Port of Unity's `PartContextQueryResult` (`AIContextEngineModel.Types.cs:54`). */
export interface PartContextQueryResult {
  readonly text: string;
  readonly docsPrompt: string | null;
  readonly response: KnowledgeContextResponse | null;
}

export class AIContextEngineModel implements IAIContextEngineModel {
  readonly onContextInvalidated = new Emitter<void>();

  private readonly m_project: IProjectModel;
  private readonly m_isWriter: () => boolean;
  private readonly m_offFns: Array<() => void> = [];

  private m_interaction: UserInteractionState = {
    selectedSnapshotIds: [],
    lastSelectionAt: 0,
    lastSelectedSnapshotIds: [],
    pointedSnapshotId: null,
    lastPointedAt: 0,
    lastPointedSnapshotId: null,
  };

  private m_cachedSceneContext: string | null = null;
  private m_cachedSceneKey: string | null = null;

  constructor(project: IProjectModel, selection: ISelectionModel, isWriter: () => boolean) {
    this.m_project = project;
    this.m_isWriter = isWriter;

    this.m_offFns.push(
      selection.onChanged.on((change) => {
        const previous = this.m_interaction.selectedSnapshotIds;
        if (previous.length > 0) {
          this.m_interaction = {
            ...this.m_interaction,
            lastSelectedSnapshotIds: previous,
            lastSelectionAt: Date.now(),
          };
        }
        this.m_interaction = {
          ...this.m_interaction,
          selectedSnapshotIds: [...change.selectedIds],
        };
      }),
      project.onEntityChangesApplied.on(() => this.invalidateCache()),
      project.onActiveSceneChanged.on(() => this.invalidateCache()),
      project.onScenesListChanged.on(() => this.invalidateCache()),
      project.onMediaTilesChanged.on(() => this.invalidateCache()),
      project.onSelectionSetsChanged.on(() => this.invalidateCache()),
    );

    // Pointing — the deictic half of User Context ("what is this?", "how many
    // of these"). Taken from IOC rather than the constructor because the hover
    // model is a viewport concern the headless context engine can run without;
    // when no viewport registered one, pointing simply never reports.
    if (Ioc.isRegistered<IHoverModel>(HOVER_MODEL)) {
      const hover = Ioc.resolve<IHoverModel>(HOVER_MODEL);
      this.m_offFns.push(
        hover.onChanged.on(({ snapshotId }) => {
          this.setPointedSnapshotId(this.resolvePointableSnapshotId(snapshotId));
        }),
      );
    }
  }

  dispose(): void {
    for (const off of this.m_offFns) off();
    this.m_offFns.length = 0;
  }

  buildSceneContext(): string {
    // ⚠ The decode generation is part of the key. The digest reports each
    // model's animation takes, which are only knowable once its glTF has
    // decoded — and placing a model into an ALREADY-loaded scene opens no
    // scene-load session, so the subscription below never fires for it. Without
    // this term the line stays takes-less for the rest of the scene's life.
    const key = `${this.m_project.activeProjectId}|${this.m_project.activeSceneId}`
      + `|${animationDecodeGeneration()}`;
    if (this.m_cachedSceneContext !== null && this.m_cachedSceneKey === key) {
      return this.m_cachedSceneContext;
    }
    const context = buildSceneContextText(this.m_project);
    this.m_cachedSceneContext = context;
    this.m_cachedSceneKey = key;
    return context;
  }

  buildProjectContext(): string {
    return buildProjectContextText(this.m_project);
  }

  buildUserContext(): string {
    return buildUserContextText(this.m_project, this.m_interaction, this.m_isWriter());
  }

  captureUserInteractionState(): UserInteractionSnapshot {
    const referencedIds = new Set<string>([
      ...this.m_interaction.selectedSnapshotIds,
      ...this.m_interaction.lastSelectedSnapshotIds,
    ]);
    if (this.m_interaction.pointedSnapshotId) {
      referencedIds.add(this.m_interaction.pointedSnapshotId);
    }
    if (this.m_interaction.lastPointedSnapshotId) {
      referencedIds.add(this.m_interaction.lastPointedSnapshotId);
    }

    const snapshotNames: Record<string, string> = {};
    const sceneId = this.m_project.activeSceneId;
    if (sceneId && referencedIds.size > 0) {
      for (const entity of this.m_project.getEntities(sceneId)) {
        for (const snapshot of entity.snapshots) {
          if (!referencedIds.has(snapshot.id)) continue;
          const name = displayNameOf(snapshot).trim();
          if (name) snapshotNames[snapshot.id] = name;
        }
      }
    }

    return Object.freeze({
      selectedSnapshotIds: Object.freeze([...this.m_interaction.selectedSnapshotIds]),
      lastSelectionAt: this.m_interaction.lastSelectionAt,
      lastSelectedSnapshotIds: Object.freeze([...this.m_interaction.lastSelectedSnapshotIds]),
      pointedSnapshotId: this.m_interaction.pointedSnapshotId,
      lastPointedAt: this.m_interaction.lastPointedAt,
      lastPointedSnapshotId: this.m_interaction.lastPointedSnapshotId,
      snapshotNames: Object.freeze(snapshotNames),
    });
  }

  /**
   * Hover reports whatever is under the cursor; POINTING is about scene
   * ENTITIES, and the two are not the same set.
   *
   * Media tiles are tagged pickable (`mediaTileRenderer.ts` — `tagSnapshot`)
   * and the viewport's hover writer filters only annotations, so a text card's
   * id genuinely arrives here. That is worse than a line that fails to
   * describe, because {@link setPointedSnapshotId} promotes whatever it
   * DISPLACES into `lastPointedSnapshotId`: an unvalidated id launders itself
   * into the referent memory on the very next move, `describe()` then answers
   * null for it, and the `else if` chain it has already entered suppresses the
   * `Last Selected Part` fallback too — so the turn reaches the agent with no
   * referent line at all. "I hovered that part and asked what it was" then
   * fails BECAUSE the cursor crossed a card on its way to the panel.
   *
   * Filtered HERE rather than at the writers, deliberately: this is the one
   * place that serves all three of them — the desktop pointer, the XR ray, and
   * `HierarchyView`'s ungated row hover — and it leaves the OUTLINE free to
   * highlight a card, which is what the viewport has always done. It also makes
   * this subscription agree with `ViewportView.resolveSparkPointerSnapshotId`,
   * which already applies exactly this check on the parallel Spark path; the
   * two writers disagreeing is what made the laundering reachable.
   */
  private resolvePointableSnapshotId(snapshotId: string | null): string | null {
    const sceneId = this.m_project.activeSceneId;
    if (!snapshotId || !sceneId) return null;
    return this.m_project.getEntityBySnapshotId(sceneId, snapshotId) === null
      ? null
      : snapshotId;
  }

  setPointedSnapshotId(snapshotId: string | null): void {
    const previous = this.m_interaction.pointedSnapshotId;
    if (previous === snapshotId) return;
    if (previous) {
      this.m_interaction = {
        ...this.m_interaction,
        lastPointedSnapshotId: previous,
        lastPointedAt: Date.now(),
      };
    }
    this.m_interaction = { ...this.m_interaction, pointedSnapshotId: snapshotId };
  }

  hasUserInteractionContext(): boolean {
    return hasUserInteractionContext(this.m_interaction);
  }

  tryBuildUserContextForMessage(): string | null {
    if (!this.hasUserInteractionContext()) return null;
    return this.buildUserContext();
  }

  tryBuildProjectContextForMessage(): string | null {
    const context = this.buildProjectContext();
    return context && context !== `{"project":null}` ? context : null;
  }

  querySceneContext(
    prompt?: string | null,
    searchQuery?: ReadonlyArray<string> | null,
  ): string {
    const full = this.buildSceneContext();
    const explicitTerms = (searchQuery ?? [])
      .map((t) => t.trim().toLowerCase())
      .filter(Boolean);
    const promptTerms = collectSearchTerms(prompt, null);

    const filterBy = (terms: string[]): string[] =>
      full.split("\n").filter((line) => {
        const lower = line.toLowerCase();
        return terms.some((t) => lower.includes(t));
      });

    if (explicitTerms.length > 0) {
      const matches = filterBy(explicitTerms);
      if (matches.length > 0) return matches.join("\n");
      // Explicit names that miss fall through to prompt terms, then full.
    }
    if (promptTerms.length > 0) {
      const matches = filterBy(promptTerms);
      if (matches.length > 0) return matches.join("\n");
    }
    if (explicitTerms.length > 0 && promptTerms.length === 0) {
      return `No matches found for: ${explicitTerms.join(", ")}. Call again without SearchQuery to get the full scene context.`;
    }
    // No usable terms (or prompt-style question): return the full digest —
    // Unity would consult the hierarchy helper agent here (deferred).
    return full;
  }

  /**
   * Unity `QueryPartContextAsync` (`Core.cs:153-211`): the part's own line
   * plus its ancestor chain becomes the "Part Context" payload, the user's
   * question plus a bottom-up name/notes priority list becomes the docs
   * prompt, and the answer is assembled as `Target` / `Notes` / `Docs`.
   */
  async queryPartContextAsync(
    prompt: string,
    partPath: string,
  ): Promise<PartContextQueryResult> {
    if (!prompt?.trim()) return { text: "error: prompt_required", docsPrompt: null, response: null };
    const sceneId = this.m_project.activeScene?.id;
    if (!sceneId) return { text: "error: no active scene", docsPrompt: null, response: null };

    const normalized = normalizePath(partPath);
    const index = buildPathIndex(this.m_project.getEntities(sceneId));
    const entry = index.find((e) => e.path === normalized);
    if (!entry) {
      return { text: `error: part not found at path '${partPath}'`, docsPrompt: null, response: null };
    }

    // The hierarchy chain, target first then ancestors — the same order Unity
    // builds it in, and the order the docs priority list reverses.
    const chain = [entry];
    let path = entry.path;
    while (path.includes(",")) {
      path = path.slice(0, path.lastIndexOf(","));
      const ancestor = index.find((e) => e.path === path);
      if (ancestor) chain.push(ancestor);
    }

    const targetLine =
      `${entry.path}: "${displayNameOf(entry.snapshot)}"`
      + `${aliasSegmentOf(entry.snapshot)}${isMoved(entry.snapshot) ? " (moved)" : ""}`;
    const partContext = [
      targetLine,
      ...chain
        .slice(1)
        .map((a) => `parent ${a.path}: "${displayNameOf(a.snapshot)}"`),
    ].join("\n");

    const trimmedPrompt = prompt.trim();
    const promptWithPath = trimmedPrompt.includes(entry.path)
      ? trimmedPrompt
      : `${trimmedPrompt} (part path ${entry.path})`;
    let docsPrompt = `${promptWithPath} | Part details: ${targetLine}`;
    docsPrompt += buildDocsInstruction(docsPrompt);

    // Unity `BuildDocsSearchPriorityInstruction`: bottom-up from the selected
    // part to its parents, so the retrieval favours the most specific name.
    const terms: string[] = [];
    const seen = new Set<string>();
    for (let i = chain.length - 1; i >= 0; i--) {
      const name = displayNameOf(chain[i].snapshot)?.trim();
      if (name && !seen.has(name.toLowerCase())) {
        seen.add(name.toLowerCase());
        terms.push(name);
      }
    }
    if (terms.length > 0 && !docsPrompt.toLowerCase().includes("docs search priority")) {
      docsPrompt +=
        ` | Docs search priority (bottom-up from selected part to parents; use name then notes/annotations): ${terms.join("; ")}`;
    }

    const response = await this.queryKnowledgeContextAsync(docsPrompt, partContext);
    const docsText = response?.text?.trim();
    const text = [
      `Target: ${targetLine}`,
      partContext,
      docsText ? `Docs:\n${docsText}` : "Docs: none",
    ].join("\n\n");
    return { text, docsPrompt, response };
  }

  // ---- Knowledge (documents) ----------------------------------------

  buildDocsPrompt(prompt: string): string {
    return buildDocsPrompt(prompt);
  }

  queryKnowledgeContextAsync(
    prompt: string,
    partContext: string,
    semanticOnly = false,
    signal?: AbortSignal,
  ): Promise<KnowledgeContextResponse> {
    return queryKnowledgeAsync(this.knowledgeDeps(), partContext, prompt, semanticOnly, signal);
  }

  searchDocsByFileNameAsync(prompt: string): Promise<KnowledgeContextResponse> {
    return searchDocsByFileNameAsync(this.knowledgeDeps(), prompt);
  }

  findPdfAsync(prompt: string, signal?: AbortSignal): Promise<KnowledgeContextResponse> {
    return findPdfAsync(this.knowledgeDeps(), prompt, signal);
  }

  /**
   * Resolved lazily and defensively: the vector store is registered only when
   * `VITE_AI_ENABLED` is on, document association comes from the loaded
   * workflow's `product`, and a cross-account project's documents live in the
   * OWNING account's partition (same precedence as `ProjectModel.getStorageContext`).
   */
  private knowledgeDeps(): KnowledgeDeps {
    return {
      docs: () =>
        Ioc.isRegistered<IProjectDocsModel>(PROJECT_DOCS_MODEL)
          ? Ioc.resolve<IProjectDocsModel>(PROJECT_DOCS_MODEL)
          : null,
      vectorStore: () =>
        Ioc.isRegistered<ICustomerVectorStoreModel>(CUSTOMER_VECTOR_STORE_MODEL)
          ? Ioc.resolve<ICustomerVectorStoreModel>(CUSTOMER_VECTOR_STORE_MODEL)
          : null,
      customerId: () => {
        const fromProject = this.m_project.activeProject?.customerId;
        if (fromProject) return fromProject;
        if (!Ioc.isRegistered<IAuthModel>(AUTH_MODEL)) return null;
        return Ioc.resolve<IAuthModel>(AUTH_MODEL).currentUser?.customerId ?? null;
      },
      productKey: () => {
        if (!Ioc.isRegistered<IProjectWorkflowModel>(PROJECT_WORKFLOW_MODEL)) return null;
        const key = normalizeProductNameKey(
          Ioc.resolve<IProjectWorkflowModel>(PROJECT_WORKFLOW_MODEL).loadedWorkflow?.product,
        );
        return key || null;
      },
      workflowId: () =>
        Ioc.isRegistered<IProjectWorkflowModel>(PROJECT_WORKFLOW_MODEL)
          ? Ioc.resolve<IProjectWorkflowModel>(PROJECT_WORKFLOW_MODEL).loadedWorkflow?.uid?.trim()
            || null
          : null,
    };
  }

  invalidateCache(): void {
    this.m_cachedSceneContext = null;
    this.m_cachedSceneKey = null;
    this.onContextInvalidated.emit();
  }
}

const PROMPT_STOP_WORDS = new Set([
  "return", "objects", "object", "identified", "the", "and", "for", "with",
  "current", "scene", "top", "level", "root", "models", "model", "parts",
  "part", "all", "list", "show", "that", "this", "are", "any",
]);

function collectSearchTerms(prompt?: string | null, _unused?: null): string[] {
  // Unity's local pass tries meaningful words from the prompt before falling
  // back to the hierarchy agent; keep words of length >= 3 minus boilerplate.
  const terms: string[] = [];
  if (prompt) {
    for (const word of prompt.toLowerCase().split(/[^a-z0-9_]+/)) {
      if (word.length >= 3 && !PROMPT_STOP_WORDS.has(word)) terms.push(word);
    }
  }
  return terms;
}

export const AI_CONTEXT_ENGINE_MODEL = Symbol.for("Campfire.AIContextEngineModel");
